<script setup>
import { Head, Link, useForm } from '@inertiajs/vue3';
import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import Checkbox from '@/Components/Checkbox.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps({
    canResetPassword: Boolean,
    status: String,
});

const form = useForm({
    username: '',
    password: '',
    remember: false,
});

const submit = () => {
    form.transform(data => ({
        ...data,
        remember: form.remember ? 'on' : '',
    })).post(route('login'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <Head :title="t('login')" />

    <AuthenticationCard>
        <p class="text-lg font-medium text-gray-900 mb-6">
            {{ t('login') }}
        </p>

        <div v-if="status" class="mb-4 font-medium text-sm text-green-600">
            {{ status }}
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel :for="'username'" :value="t('username')" />
                <TextInput
                    id="username"
                    v-model="form.username"
                    type="text"
                    class="mt-1 block w-full"
                    required
                    autofocus
                    autocomplete="username"
                />
                <InputError class="mt-2" :message="form.errors.username" />
            </div>

            <div class="mt-4">
                <InputLabel :for="'password'" :value="t('password')" />
                <TextInput
                    id="password"
                    v-model="form.password"
                    type="password"
                    class="mt-1 block w-full"
                    required
                    autocomplete="current-password"
                />
                <InputError class="mt-2" :message="form.errors.password" />
            </div>

            <div class="block mt-4">
                <label class="flex items-center">
                    <Checkbox v-model:checked="form.remember" name="remember" />
                    <span class="ms-2 text-sm text-gray-600">{{ t('remember') }}</span>
                </label>
            </div>

            <PrimaryButton
                class="w-full justify-center mt-6 bg-[#1A2E2B] hover:bg-[#24403B] focus:ring-[#1A2E2B]"
                :class="{ 'opacity-25': form.processing }"
                :disabled="form.processing"
            >
                {{ t('login') }}
            </PrimaryButton>

            <div class="text-center mt-4" v-if="canResetPassword">
                <Link
                    :href="route('password.request')"
                    class="text-sm text-[#4A6B63] hover:text-[#1A2E2B] underline"
                >
                    {{ t('forgot_password') }}
                </Link>
            </div>
        </form>
    </AuthenticationCard>
</template>